import type { Copy } from './en'
import { en } from './en'
import { ha } from './ha'

export const LOCALE_CODES = ['en', 'ha'] as const
export type LocaleCode = (typeof LOCALE_CODES)[number]

export interface LocaleDescriptor {
  code: LocaleCode
  /** Primary BCP-47 tag. Used for `<html lang>` and as the first voice guess. */
  bcp47: string
  /** Written in the language itself, because that is what a speaker looks for. */
  nativeName: string
  englishName: string
  direction: 'ltr' | 'rtl'
  /**
   * Whether a native speaker has signed off on this locale's copy. Locales that
   * have not been reviewed are labelled as a preview in the UI rather than
   * being presented as finished work.
   */
  reviewed: boolean
  /**
   * Tags to try, in order, when hunting for a platform speech voice or
   * recogniser. Browsers are inconsistent about regional tags, so we try
   * specific first and fall back to the bare language.
   */
  speechTags: readonly string[]
  /**
   * What to do when the platform has no voice at all for this language.
   * "caption" means: keep the assistant's words on screen and do not fake it
   * with a wrong-language voice. Getting this wrong is worse than silence for
   * a product whose entire premise is speaking people's own languages.
   */
  noVoiceStrategy: 'caption' | 'fallback-voice'
}

export const LOCALES: Record<LocaleCode, LocaleDescriptor> = {
  en: {
    code: 'en',
    bcp47: 'en-NG',
    nativeName: 'English',
    englishName: 'English',
    direction: 'ltr',
    reviewed: true,
    speechTags: ['en-NG', 'en-GB', 'en-US', 'en'],
    noVoiceStrategy: 'fallback-voice',
  },
  ha: {
    code: 'ha',
    bcp47: 'ha-NG',
    nativeName: 'Hausa',
    englishName: 'Hausa',
    direction: 'ltr',
    reviewed: false,
    speechTags: ['ha-NG', 'ha-NE', 'ha'],
    // Almost no browser ships a Hausa voice today. Reading Hausa aloud with an
    // English voice is not a graceful degradation, it is a mispronunciation, so
    // we caption instead until a real Namu voice is wired up.
    noVoiceStrategy: 'caption',
  },
}

export const COPY: Record<LocaleCode, Copy> = { en, ha }

/**
 * The language the product opens in, for everybody, on every device.
 *
 * This used to be read from `navigator.languages`, and reading it there was
 * the mistake. A phone in Niger or northern Nigeria is very often sold, set up
 * and left in French or English by somebody other than the person holding it,
 * so the device's language is evidence about the shop rather than about the
 * speaker. Deriving the interface from it hands a Hausa speaker a product
 * built for them in a language they did not pick, at the one moment they are
 * deciding whether it was built for them at all.
 *
 * So it is a decision rather than a detection, and Hausa is the decision.
 * English is still here, still translated and still one row away in Settings,
 * and the first reply can settle it without anybody opening Settings at all:
 * see `src/onboarding/detectLocale.ts`. What has gone is the guess.
 */
export const DEFAULT_LOCALE: LocaleCode = 'ha'

export function isLocaleCode(value: unknown): value is LocaleCode {
  return typeof value === 'string' && (LOCALE_CODES as readonly string[]).includes(value)
}
